import "@formatjs/intl-numberformat/locale-data/en";
import "@formatjs/intl-numberformat/polyfill";
import { StyledEngineProvider } from "@mui/material";
import { useLocation } from "@reach/router";
import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { CookieStorage } from "aws-amplify/utils";
import "bootstrap/dist/css/bootstrap.min.css";
import { navigate } from "gatsby";
import { useSnackbar } from "notistack";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import { IntlProvider } from "react-intl";
import axiosClient, { checkIsPrivateRoute } from "../../api/axiosClient";
import { getCompanyInfo } from "../../api/company";
import { getMeApi } from "../../api/users.ts";
import {
  AUTHENTICATED_REDIRECT_PAGES,
  INITIAL_APP_STATE,
  NOT_DASHBOARD_ROUTES,
} from "../../commons/enum";
import { APP_LAYOUT_TYPES } from "../../commons/layout-types.constants";
import { SIGN_UP_PROCESS_STEP } from "../../commons/signup.constants";
import CheckUserLoggedInContext from "../../context/check-user-logged-in-context";
import DataLoadingContext from "../../context/data-loading-context";
import { FinanceContext } from "../../context/finance-context";
import JointAccountContext from "../../context/joint-account-context";
import { LoanApplicationLayoutProvider } from "../../context/loan-application-layout-context";
import { SelectedDraftLoanProvider } from "../../context/selected-draft-loan-context";
import {
  setQueryDataForDemo,
  updateThemeAfterSetMockupData,
} from "../../demo/demo-utils/build-demo-data";
import "../../fonts/font.css";
import { useCompanyInfo, useIsScreenMd } from "../../hooks";
import { useScenarioList } from "../../hooks/scenario.hooks";
import { useIsLoggedIn, useMyInfo } from "../../hooks/user.hooks.ts";
import LoanApplicationLayout from "../../pages/loan-application/components/layout";
import PreLoanApplicationLayout from "../../pages/loan-application/components/pre-loan-layout";
import "../../scss/account.scss";
import "../../scss/application-process.scss";
import "../../scss/chart.scss";
import "../../scss/finance.scss";
import "../../scss/help-support.scss";
import "../../scss/layout.scss";
import "../../scss/loan-application.scss";
import "../../scss/login.scss";
import "../../scss/normalize.css";
import "../../scss/sharing-referral.scss";
import "../../scss/utilities.scss";
import {
  MobileMessageBridge,
  MobileMessageType,
  getcompanyDomain,
  mobileMessageBridge,
  parseQueryParams,
} from "../../utils";
import {
  getCsrfToken,
  logout,
  updateUserLastLoginTime,
} from "../../utils/auth";
import {
  checkIsDemoClient,
  getValueFromURLWithKey,
} from "../../utils/get-params-value";
import {
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
  setItemLocalStorage,
} from "../../utils/miscellaneous-util";
import { queryClient } from "../../utils/react-query-util";
import {
  currentAuthenticatedUser,
  currentSession,
} from "../../utils/user-auth-provider";
import { AuthenticatedRedirect } from "../authenticated-redirect";
import CustomButton from "../custom-button";
import Footer from "../footer";
import { HideInEmbedMode } from "../hide-in-embed-mode";
import { LoadingBackdrop } from "../loading-backdrop";
import MenuLeft from "../menu_left";
import SentryIdentity from "../SentryIdentity/index.tsx";
import SEO from "../SEO";
import SharingLayout from "../sharing-layout";
import SignupBanner from "../signup-banner-left";
import BasiqExpireNotificationDialog from "./basiq-expiring-notification-dialog";
import { DashboardLogicWrapper } from "./dashboard-logic-wrapper";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.GATSBY_COGNITO_USER_POOL_ID,
      userPoolClientId: process.env.GATSBY_COGNITO_APP_CLIENT_ID,
    },
  },
});
cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: process.env.GATSBY_SHARED_COOKIE_DOMAIN,
    secure: !(process.env.GATSBY_SHARED_COOKIE_DOMAIN || "").includes(
      "localhost"
    ),
    path: "/",
    expires: 365,
    sameSite: "lax",
  })
);

const getStatusDataReducer = (state, action) => {
  switch (action.type) {
    case "appState":
      return { ...state, ...action.value };
    default:
      throw new Error("Unexpected action");
  }
};

const initializeApp = async (location) => {
  const isDemoClient = checkIsDemoClient();
  const DEFAULT_INITIALIZATION_RESULT = {
    viewAsUserId: "",
    canViewAsUser: false,
    shouldGoToLogin: false,
    authUserName: "",
  };
  let initializationResult = DEFAULT_INITIALIZATION_RESULT;
  const viewAsUserIdInURL = getValueFromURLWithKey(
    "viewAsUserId",
    location.href
  );

  const _viewAsUserId =
    viewAsUserIdInURL || getItemFromLocalStorage("viewAsUserId");

  if (viewAsUserIdInURL) {
    setItemLocalStorage("viewAsUserId", viewAsUserIdInURL);
  }

  const authUserNameInURL = getValueFromURLWithKey(
    "authUserName",
    location.href
  );
  const _authUserName =
    authUserNameInURL || getItemFromLocalStorage("authUserName");
  if (authUserNameInURL) {
    setItemLocalStorage("authUserName", authUserNameInURL);
  }

  try {
    await getCsrfToken();
    const companyDomain = getcompanyDomain();
    // redirect backoffice to page error because getCompanyDomain api will return success if domain = backoffice, which will treat it as a company rather than a backoffice admin
    if (companyDomain === "backoffice") {
      navigate("/page-error");
      return;
    }
    await getCompanyInfo({
      params: { domain: companyDomain },
    });
    const authUser = await currentAuthenticatedUser();
    const authUserName = authUser.username;
    const session = await currentSession();
    const accessToken = session.tokens.accessToken.toString();
    axiosClient.defaults.headers.common.authentication = accessToken;
    if (_viewAsUserId) {
      if (_authUserName === authUserName) {
        axiosClient.defaults.headers.common["view-as-user"] = _viewAsUserId;
        initializationResult = {
          viewAsUserId: _viewAsUserId,
          canViewAsUser: true,
          shouldGoToLogin: false,
          authUserName: _authUserName,
        };
      } else {
        initializationResult = {
          viewAsUserId: _viewAsUserId,
          canViewAsUser: false,
          shouldGoToLogin: false,
          authUserName: _authUserName,
        };
      }
      window.history.replaceState(null, "", location.pathname);
    } else {
      updateUserLastLoginTime();
    }
    await queryClient.fetchQuery("myInfo", getMeApi);
  } catch (err) {
    const isInDashboardRoutes = NOT_DASHBOARD_ROUTES.every(
      (item) => location.pathname.indexOf(item) < 0
    );
    if (isInDashboardRoutes) {
      initializationResult = {
        viewAsUserId: _viewAsUserId,
        canViewAsUser: false,
        shouldGoToLogin: true,
        authUserName: _authUserName,
      };
    }
    if (
      err.data?.message?.includes("waiting for approval") ||
      err.data?.message?.includes("not existed")
    ) {
      initializationResult.shouldGoToLogin = false;
    }
    if (isDemoClient) {
      // eslint-disable-next-line consistent-return
      return DEFAULT_INITIALIZATION_RESULT;
    }
  }
  // eslint-disable-next-line consistent-return
  return initializationResult;
};

const setupQueryCacheNotification = () => {
  const listenerForInvalidation = MobileMessageBridge.createListener(
    (message) => {
      if (message?.data.queryKey) {
        queryClient.invalidateQueries(message.data.queryKey);
      }
    },
    [MobileMessageType.INVALIDATE_QUERY]
  );

  mobileMessageBridge.register(listenerForInvalidation);

  return () => {
    mobileMessageBridge.unregister(listenerForInvalidation.id);
  };
};

export const Layout = ({ children, location, pageContext }) => {
  const [selectedJointAccount, setSelectedJointAccount] = useState("");
  const [appState, appStateDispatch] = useReducer(
    getStatusDataReducer,
    INITIAL_APP_STATE
  );
  const { search } = useLocation();
  const [isInitializing, setIsInitializing] = useState(true);
  const { data: userInfo = {} } = useMyInfo({ pending: isInitializing });
  const isLoggedIn = useIsLoggedIn();
  const { enqueueSnackbar } = useSnackbar();
  const setAppState = useCallback(
    (data) => appStateDispatch({ type: "appState", value: data }),
    []
  );
  const [checkUserLoggedIn, setCheckUserLoggedIn] = useState(false);
  const [selectedScenarioId, setSelectedScenarioId] = useState("");
  const { data: scenarioList = [] } = useScenarioList(selectedJointAccount, {
    pending: isInitializing,
    retry: (failureCount, error) => {
      // Don't retry on 401 errors
      if (error?.status === 401) {
        return false;
      }
      // Default retry behavior for other errors
      return failureCount < 2;
    },
  });
  const isScreenMd = useIsScreenMd();
  const selectedScenarioDetail = React.useMemo(() => {
    return scenarioList?.find((scenario) => scenario.id === selectedScenarioId);
  }, [scenarioList, selectedScenarioId]);

  const urlParams = useMemo(() => new URLSearchParams(search), [search]);
  const localStorageData = JSON.parse(
    decodeURIComponent(urlParams.get("data"))
  );

  const redirect = useMemo(() => {
    return urlParams.get("redirect") || "/";
  }, [urlParams]);

  const { isLoading: isCompanyLoading, isError: isCompanyError } =
    useCompanyInfo();

  const financeContextValue = useMemo(() => {
    return {
      selectedScenarioId,
      setSelectedScenarioId,
      selectedScenarioDetail,
    };
  }, [selectedScenarioDetail, selectedScenarioId, setSelectedScenarioId]);

  const pathname = location.pathname.replace(/\//g, "");
  const isPageSignUpWithSelectedProcess = useMemo(() => {
    const signUpProcess = urlParams.get("signup-process");
    return (
      pathname === "signup" &&
      signUpProcess &&
      Object.values(SIGN_UP_PROCESS_STEP).includes(signUpProcess)
    );
  }, [pathname, urlParams]);

  const onResizeHandler = () => {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      const pageLayout = document.documentElement;
      const vh = window.innerHeight * 0.01;
      pageLayout.current?.style.setProperty("--vh", `${vh}px`);
    }
  };

  const [viewAsUserId, setViewAsUserId] = useState("");
  const exitViewAsUser = () => {
    removeItemFromLocalStorage("viewAsUserId");
    removeItemFromLocalStorage("authUserName");
    window.location.reload();
  };

  const isDemoClient = checkIsDemoClient();

  useLayoutEffect(() => {
    axiosClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error?.response) {
          if (
            error.response.status === 401 &&
            error.response.data?.message === "Unauthorized" &&
            error.config.url.indexOf("logout") < 0
          ) {
            try {
              const session = await currentSession();
              const apiConfig = {
                ...error.config,
                headers: {
                  ...error.config.headers,
                  authentication: session.tokens.accessToken.toString(),
                },
              };
              axiosClient.defaults.headers.common.authentication =
                session.tokens.accessToken.toString();
              return axiosClient(apiConfig);
            } catch (err) {
              if (
                err === "No current user" &&
                checkIsPrivateRoute() &&
                !isDemoClient
              ) {
                logout();
              }
            }
          }
          if (
            error.response.status === 403 &&
            error.response.data?.errorCode === "CFRSTOKEN_EXPIRED"
          ) {
            const csrfToken = await getCsrfToken();
            const apiConfig = {
              ...error.config,
              headers: {
                ...error.config.headers,
                "csrf-token": csrfToken,
              },
            };
            axiosClient.defaults.headers.common["csrf-token"] = csrfToken;
            return axiosClient(apiConfig);
          }
          if (
            error.response.status === 500 &&
            error.response.data?.message === "Basiq Error" &&
            error.response.data?.error !== "Requested consent does not exist."
          ) {
            enqueueSnackbar(error.response.data?.error || "Basiq Error", {
              variant: "error",
            });
          }
          throw error.response;
        }
        throw error;
      }
    );
  }, [isDemoClient, enqueueSnackbar]);

  useLayoutEffect(() => {
    if (isDemoClient) {
      setQueryDataForDemo();
      updateThemeAfterSetMockupData();
      axiosClient.interceptors.response.use(
        (response) => response,
        async (error) => {
          if (
            !error.config.url.includes("index") &&
            !error.config.url.includes("staffInfoByCode")
          ) {
            enqueueSnackbar("Can't do this action in Demo version", {
              variant: "error",
              persist: false,
            });
            throw new Error("Can't do this action in Demo version");
          }
        }
      );
    }
  }, [enqueueSnackbar, isDemoClient]);

  useLayoutEffect(() => {
    if (!isDemoClient && localStorageData) {
      const entries = Object.entries(localStorageData);
      entries.forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });
      window.history.replaceState(null, "", "/");
    }
    let unsubscribeQueryCacheNotification;
    setIsInitializing(true);
    initializeApp(location)
      .then((initializationResult = {}) => {
        if (initializationResult.viewAsUserId) {
          if (initializationResult.canViewAsUser) {
            setViewAsUserId(initializationResult.viewAsUserId);
          } else {
            if (!initializationResult.shouldGoToLogin) {
              enqueueSnackbar(
                "This account is not allowed to view. Please login with the permitted account and try again",
                { variant: "warning", duration: 10000 }
              );
            }
            navigate(
              `/login/?viewAsUserId=${initializationResult.viewAsUserId}&authUserName=${initializationResult.authUserName}`
            );
            return;
          }
        }
        if (initializationResult.shouldGoToLogin) {
          window.location.href = "/login";
        }
      })
      .finally(() => {
        const queryParams = parseQueryParams(location.search);
        if (queryParams.get("embed") === "true") {
          setAppState({ isInEmbedMode: true });
          unsubscribeQueryCacheNotification = setupQueryCacheNotification();
        }
        const selectedJointAccountFromParams = queryParams.get(
          "selectedJointAccount"
        );
        if (selectedJointAccountFromParams) {
          setSelectedJointAccount(selectedJointAccountFromParams);
        }
        setIsInitializing(false);
      });

    return () => {
      unsubscribeQueryCacheNotification?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", onResizeHandler);
    }
  }, []);

  const renderPage = () => {
    switch (pageContext.layout) {
      case APP_LAYOUT_TYPES.LOGIN_LAYOUT: {
        if (isCompanyLoading || isCompanyError) {
          return <LoadingBackdrop />;
        }
        const page = (
          <div className="my-account-body">
            <div className="login-wrapper">
              {!isScreenMd && <SignupBanner />}
              {children}
              <Footer />
            </div>
          </div>
        );
        if (
          AUTHENTICATED_REDIRECT_PAGES.includes(pathname) &&
          !isPageSignUpWithSelectedProcess
        ) {
          return (
            <AuthenticatedRedirect redirectTo={redirect}>
              {page}
            </AuthenticatedRedirect>
          );
        }
        return page;
      }
      case APP_LAYOUT_TYPES.SHARING_LAYOUT: {
        return <SharingLayout>{children}</SharingLayout>;
      }
      case APP_LAYOUT_TYPES.ERROR_LAYOUT: {
        return children;
      }
      case APP_LAYOUT_TYPES.PRE_LOAN_APPLICATION_LAYOUT: {
        if (isCompanyLoading || isCompanyError) {
          return <LoadingBackdrop />;
        }
        return (
          <SelectedDraftLoanProvider>
            <PreLoanApplicationLayout location={location}>
              {children}
            </PreLoanApplicationLayout>
          </SelectedDraftLoanProvider>
        );
      }
      case APP_LAYOUT_TYPES.LOAN_APPLICATION_LAYOUT: {
        return (
          <SelectedDraftLoanProvider>
            <>
              {isLoggedIn && viewAsUserId && (
                <div className="view-as-user__container">
                  <div className="view-as-user__label">
                    You&apos;re watching{" "}
                    <span>
                      {userInfo?.firstName} ({userInfo?.email})
                    </span>{" "}
                    profiles
                  </div>
                  <CustomButton label="Exit view as" onClick={exitViewAsUser} />
                </div>
              )}
              <LoanApplicationLayoutProvider>
                <LoanApplicationLayout location={location}>
                  {children}
                </LoanApplicationLayout>
              </LoanApplicationLayoutProvider>
            </>
          </SelectedDraftLoanProvider>
        );
      }
      case APP_LAYOUT_TYPES.DASHBOARD_LAYOUT:
      default: {
        if (isCompanyLoading || isCompanyError) {
          return <LoadingBackdrop />;
        }
        return (
          isLoggedIn && (
            <DashboardLogicWrapper location={location}>
              {viewAsUserId && (
                <div className="view-as-user__container">
                  <div className="view-as-user__label">
                    You&apos;re watching{" "}
                    <span>
                      {userInfo?.firstName} ({userInfo?.email})
                    </span>{" "}
                    profiles
                  </div>
                  <CustomButton label="Exit view as" onClick={exitViewAsUser} />
                </div>
              )}
              {!pageContext?.showBlankLayout && (
                <div className="layout-container">
                  <HideInEmbedMode>
                    <MenuLeft location={location} />
                  </HideInEmbedMode>
                  <div className="my-account-body">
                    <div className="my-account-content">{children}</div>
                    <Footer />
                  </div>
                </div>
              )}
              {pageContext?.showBlankLayout && children}

              <BasiqExpireNotificationDialog />
            </DashboardLogicWrapper>
          )
        );
      }
    }
  };
  const checkUserLoggedInContextValue = useMemo(
    () => ({ checkUserLoggedIn, setCheckUserLoggedIn }),
    [checkUserLoggedIn, setCheckUserLoggedIn]
  );
  const jointAccountContextValue = useMemo(
    () => ({ selectedJointAccount, setSelectedJointAccount }),
    [selectedJointAccount, setSelectedJointAccount]
  );
  const dataLoadingContextValue = useMemo(
    () => ({ ...appState, setAppState }),
    [appState, setAppState]
  );
  if (isInitializing || isCompanyLoading) {
    return <LoadingBackdrop />;
  }

  return (
    <StyledEngineProvider injectFirst>
      <CheckUserLoggedInContext.Provider value={checkUserLoggedInContextValue}>
        <JointAccountContext.Provider value={jointAccountContextValue}>
          <DataLoadingContext.Provider value={dataLoadingContextValue}>
            <SentryIdentity />
            <FinanceContext.Provider value={financeContextValue}>
              <SEO />
              <IntlProvider defaultLocale="en" locale="en">
                {renderPage()}
              </IntlProvider>
            </FinanceContext.Provider>
          </DataLoadingContext.Provider>
        </JointAccountContext.Provider>
      </CheckUserLoggedInContext.Provider>
    </StyledEngineProvider>
  );
};
